import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { palette, FONT } from '../../styles/tourTheme';
import { pickTourDayCard, DAY_KIND_ACCENT } from '../../utils/calendarDayCard';

/**
 * Month/week day-card body from the Master Tour Calendar spec:
 * kind (Show / Travel / Off), city, venue, up to two chips.
 */
export function TourDayCellHeader({
  card,
  isDarkMode = false,
  compact = false,
  muted = false,
  hideKind = false,
  hideChips = false,
}) {
  if (!card?.kind) return null;
  const pal = palette(isDarkMode);
  const styles = makeStyles(pal, compact);
  return (
    <View style={[styles.wrap, muted && styles.muted]} pointerEvents="none">
      {card.kindLabel && !hideKind ? (
        <View style={styles.kindRow}>
          <Text style={styles.kind}>{card.kindLabel}</Text>
        </View>
      ) : null}
      {card.cityLine ? (
        <Text style={styles.city} numberOfLines={compact || hideChips ? 1 : 2}>
          {card.cityLine}
        </Text>
      ) : null}
      {card.venueLine ? (
        <Text style={styles.venue} numberOfLines={compact || hideChips ? 1 : 2}>
          {card.venueLine}
        </Text>
      ) : null}
      {!hideChips && card.chips?.length ? (
        <View style={styles.chips}>
          {card.chips.map((chip) => (
            <View key={chip.text} style={styles.chip}>
              <Text style={styles.chipText}>{chip.text}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

/** Reserved 36px chip row pinned to the bottom of a month poster cell. */
export function DayCellChipFooter({ chips, isDarkMode = false, onChipAction }) {
  const pal = palette(isDarkMode);
  const styles = makeStyles(pal, false);
  const list = Array.isArray(chips) ? chips.slice(0, 2) : [];
  return (
    <View style={styles.chipFooter} pointerEvents={onChipAction ? 'box-none' : 'none'}>
      {list.map((chip) => {
        const inner = <Text style={styles.chipText}>{chip.text}</Text>;
        if (chip.action && onChipAction) {
          return (
            <TouchableOpacity
              key={chip.text}
              onPress={(e) => {
                e?.stopPropagation?.();
                onChipAction(chip.action);
              }}
              style={styles.chip}
              accessibilityRole="button"
              accessibilityLabel={chip.text}
            >
              {inner}
            </TouchableOpacity>
          );
        }
        return (
          <View key={chip.text} style={styles.chip} pointerEvents="none">
            {inner}
          </View>
        );
      })}
    </View>
  );
}

function makeStyles(pal, compact) {
  return StyleSheet.create({
    wrap: {
      width: '100%',
      paddingTop: compact ? 0 : 2,
      paddingBottom: compact ? 0 : 0,
      gap: compact ? 1 : 2,
    },
    muted: { opacity: 0.55 },
    kindRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
    },
    kind: {
      fontFamily: FONT.mono,
      fontSize: compact ? 9.5 : 10,
      fontWeight: '500',
      letterSpacing: 1,
      textTransform: 'uppercase',
      color: pal.faint,
    },
    city: {
      fontFamily: FONT.sans,
      fontSize: compact ? 13.5 : 15,
      fontWeight: '600',
      letterSpacing: -0.2,
      color: pal.ink,
      lineHeight: compact ? 16 : 18,
    },
    venue: {
      fontFamily: FONT.sans,
      fontSize: 12.5,
      fontWeight: '400',
      color: pal.mute,
      lineHeight: 16,
    },
    chips: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 4,
      marginTop: compact ? 2 : 6,
    },
    chipFooter: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      columnGap: 4,
      rowGap: 4,
      minHeight: 24,
      alignItems: 'flex-end',
      width: '100%',
      flexGrow: 0,
      flexShrink: 0,
      marginTop: 'auto',
      paddingTop: 4,
      overflow: 'visible',
    },
    chip: {
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 4,
      backgroundColor: pal.page,
    },
    chipText: {
      fontFamily: FONT.mono,
      fontSize: 11,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: pal.mute,
    },
  });
}

/**
 * Month-cell card: optional multi-tour dots + the picked tour's Show/Travel/Off header.
 */
export function DayCellTourCard({
  meta,
  selectedTourFilterId,
  isDarkMode = false,
  compact = false,
  muted = false,
  hideKind = false,
  hideChips = false,
}) {
  const card = pickTourDayCard(meta, selectedTourFilterId);
  const tours = Array.isArray(meta?.tours) ? meta.tours : [];
  const showDots = tours.length > 1 && !selectedTourFilterId;
  if (!card?.kind && !showDots) return null;
  return (
    <View style={{ width: '100%' }} pointerEvents="none">
      {showDots ? (
        <View style={dotStyles.row}>
          {tours.slice(0, 4).map((t) => (
            <View
              key={t.tourId}
              style={[dotStyles.dot, { backgroundColor: t.tourColor || DAY_KIND_ACCENT.show }]}
            />
          ))}
        </View>
      ) : null}
      <TourDayCellHeader
        card={card}
        isDarkMode={isDarkMode}
        compact={compact}
        muted={muted}
        hideKind={hideKind}
        hideChips={hideChips}
      />
    </View>
  );
}

const dotStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 4,
    marginBottom: 2,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
});

export default TourDayCellHeader;
